import { bpmnId } from '@caliber-2/discovery-core/maps';
import type {
  FutureMetricsDto,
  FutureTag,
  MapCompare,
  MapCompareTable,
  MapStepDto,
  VendorInfoChange,
} from '@/lib/api/process-maps';
import { proposedMap, type MapModel, type Markers } from './map-logic';
import type { ChangeContext, ChangePreview } from '@caliber-2/discovery-core/maps';

/**
 * Future-state maps (T-0208, E13-F09, ADR-020): what the screen draws from
 * the server's answers. Nothing here counts or tags: change tags, removed
 * steps, newer vendor information and every number come from the API,
 * computed by code there (rule 26). These helpers only turn them into marks
 * on the canvas and words on the screen.
 */

/** The three change-set operations only a future map takes (ADR-020 §4). */
export const FUTURE_OPERATIONS = [
  'link_vendor_knowledge',
  'unlink_vendor_knowledge',
  'set_assumption',
] as const;

export const TAG_LABEL: Record<FutureTag, string> = {
  unchanged: 'Unchanged',
  changed: 'Changed',
  automated: 'Automated',
  new: 'New',
};

function mark(out: Markers, id: string, cls: string): void {
  (out[id] ??= []).includes(cls) || out[id]!.push(cls);
}

type TaggedStep = Pick<MapStepDto, 'key' | 'tag' | 'workaround' | 'is_assumption'>;

/**
 * A future map's marks, from the server's tags (OQ-V: colour and outline on
 * the shapes, never tags over them): changed, automated, new, workaround,
 * assumption, and newer vendor information on the steps that cite it.
 */
export function futureMarkers(
  steps: ReadonlyArray<TaggedStep>,
  vendorInfo: ReadonlyArray<VendorInfoChange> = [],
): Markers {
  const out: Markers = {};
  for (const s of steps) {
    const id = bpmnId('step', s.key);
    if (s.tag && s.tag !== 'unchanged') mark(out, id, `mk-f-${s.tag}`);
    if (s.workaround) mark(out, id, 'mk-f-workaround');
    if (s.is_assumption) mark(out, id, 'mk-f-assumption');
  }
  for (const c of vendorInfo) mark(out, bpmnId('step', c.step_key), 'mk-vendor-info');
  return out;
}

/** Today's map beside a future one: the steps the future state removes or changes. */
export function todayMarkers(steps: MapCompare['today']['steps']): Markers {
  const out: Markers = {};
  for (const s of steps) {
    if (s.tag === 'removed') mark(out, bpmnId('step', s.key), 'mk-f-removed');
    if (s.tag === 'changed') mark(out, bpmnId('step', s.key), 'mk-f-changed');
  }
  return out;
}

/**
 * The map a proposed change set to a future map would leave, for the
 * preview. Lanes, steps and flows are applied by the map engine as for a
 * current map; the three future operations change only what a step cites
 * (the server checks them against the product's items), so the steps they
 * name are shown as changed. Null when the engine refuses the structure.
 */
export function futureProposal(
  base: MapModel,
  operations: ReadonlyArray<Record<string, unknown>>,
  context: ChangeContext,
): { model: MapModel; preview: ChangePreview } | null {
  const isFuture = (op: Record<string, unknown>) =>
    (FUTURE_OPERATIONS as readonly unknown[]).includes(op['op']);
  const structural = operations.filter((op) => !isFuture(op));
  const cited = operations.filter(isFuture).map((op) => String(op['step']));
  const applied = structural.length > 0 ? proposedMap(base, structural, context) : null;
  if (structural.length > 0 && !applied) return null;
  const preview: ChangePreview = applied?.preview ?? {
    added: [],
    removed: [],
    renamed: [],
    moved: [],
    flows_changed: [],
    lanes_changed: [],
    citations_changed: [],
  };
  return {
    model: applied?.model ?? base,
    preview: {
      ...preview,
      citations_changed: [...new Set([...preview.citations_changed, ...cited])],
    },
  };
}

function slug(name: string): string {
  return (
    name
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'map'
  );
}

/** `<process>-<product>-v<n>.bpmn`, when the server does not name the file. */
export function futureFileName(process: string, product: string, versionNo: number): string {
  return `${slug(process)}-${slug(product)}-v${versionNo}.bpmn`;
}

/** The steps newer vendor information touches, once each, in the map's order. */
export function affectedSteps(
  changes: ReadonlyArray<VendorInfoChange>,
  steps: ReadonlyArray<Pick<MapStepDto, 'key' | 'name'>>,
): Array<{ key: string; name: string }> {
  const keys = new Set(changes.map((c) => c.step_key));
  return steps.filter((s) => keys.has(s.key)).map((s) => ({ key: s.key, name: s.name }));
}

const FLAG_TEXT: Record<string, string> = {
  source_changed: 'its source changed',
  source_unavailable: 'its source can no longer be read',
  not_found_on_source: 'its words are no longer in the source',
  client_name: 'it may name a client',
};

/** "VK-00412 is superseded by VK-01802." */
export function changeText(c: VendorInfoChange): string {
  const item = c.item_number ?? 'A cited item';
  if (c.state === 'superseded')
    return c.successor_number
      ? `${item} is superseded by ${c.successor_number}.`
      : `${item} is superseded.`;
  if (c.state === 'retired') return `${item} is retired.`;
  const why = c.flag_reason ? (FLAG_TEXT[c.flag_reason] ?? c.flag_reason.replace(/_/g, ' ')) : '';
  return why ? `${item} is flagged: ${why}.` : `${item} is flagged.`;
}

/** The step on the other side of Compare with today: same key, or none (removed or new). */
export function pairedKey(
  pairs: MapCompare['pairs'],
  side: 'today' | 'future',
  key: string,
): string | null {
  const p = pairs.find((x) => (side === 'today' ? x.current_key : x.future_key) === key);
  return p ? (side === 'today' ? p.future_key : p.current_key) : null;
}

type Row = MapCompareTable['rows'][number];

/**
 * The comparison table's columns, the prototype's like-for-like counts;
 * today has no future-only counts. Each map's evidence counts (cited,
 * assumed, removed) are shown beside the map itself.
 */
export const COMPARE_COLUMNS: ReadonlyArray<{
  label: string;
  value: (row: Row) => number | null;
}> = [
  { label: 'Steps people do', value: (r) => r.metrics.people },
  { label: 'On paper or in a spreadsheet', value: (r) => r.metrics.paper },
  { label: 'Steps the system does', value: (r) => r.metrics.system },
  { label: 'Hand-offs between people', value: (r) => r.metrics.handoffs },
  {
    label: 'Pain points on removed or changed steps',
    value: (r) => (r.kind === 'future' ? r.metrics.pain_points_on_removed_or_changed : null),
  },
  { label: 'Workarounds', value: (r) => (r.kind === 'future' ? r.metrics.workarounds : null) },
];

export type { FutureMetricsDto };
